import {
  borderRadius,
  iconSize,
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { Box, IconButton } from '@mui/material';
import { buttonClasses } from '@mui/material/Button';
import { dialogContentClasses } from '@mui/material/DialogContent';
import { X } from 'lucide-react';
import type { ConfirmOptions, ConfirmResult } from 'material-ui-confirm';
import { useConfirm } from 'material-ui-confirm';
import type React from 'react';
import { useCallback } from 'react';

type ModConfirmOptions = {
  title?: string;
  description?: React.ReactNode;
  confirmationText?: string;
  cancellationText?: string;
  /** Use destructive styling for confirm button */
  destructive?: boolean;
  /** Additional props for the confirmation button (e.g., disabled) */
  confirmationButtonProps?: {
    disabled?: boolean;
  };
};

/**
 * Thin wrapper around useConfirm that applies design-system styling
 *
 * @example
 * const confirm = useModConfirm();
 *
 * const handleDelete = async () => {
 *   const { confirmed } = await confirm({
 *     title: 'Remove repository',
 *     description: 'Are you sure you want to remove the selected repositories?',
 *     confirmationText: 'Remove',
 *     destructive: true
 *   });
 *
 *   if (confirmed) {
 *     // perform delete
 *   }
 * };
 */
export const useModConfirm = (): ((
  options?: ModConfirmOptions
) => Promise<ConfirmResult>) => {
  const confirm = useConfirm();

  return useCallback(
    (options?: ModConfirmOptions) => {
      const handleClose = (event: React.MouseEvent) => {
        // Find the dialog's cancel button and click it
        const dialog = (event.target as HTMLElement).closest('.MuiDialog-root');
        const cancelButton = dialog?.querySelector(
          `.${buttonClasses.text}`
        ) as HTMLButtonElement | null;
        cancelButton?.click();
      };

      const modOptions: ConfirmOptions = {
        title: (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <span>{options?.title}</span>
            <IconButton
              onClick={handleClose}
              aria-label="Close"
              sx={{
                color: semanticColors.color.icon.utility,
                p: 0,
                width: 24,
                height: 24,
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: semanticColors.color.icon.utility
                }
              }}
            >
              <X size={iconSize.xxl} />
            </IconButton>
          </Box>
        ),
        description: options?.description,
        confirmationText: options?.confirmationText ?? 'Confirm',
        cancellationText: options?.cancellationText ?? 'Cancel',
        allowClose: true,
        dialogProps: {
          maxWidth: 'sm',
          slotProps: {
            paper: {
              sx: {
                borderRadius: `${borderRadius.card}px`
              }
            }
          },
          sx: {
            zIndex: (theme) => theme.zIndex.dialogNested,
            // hide borders on dialog content
            [`& .${dialogContentClasses.root}`]: {
              borderTop: 'none',
              borderBottom: 'none'
            }
          }
        },
        titleProps: {
          sx: {
            fontSize: typography.fontSize.h3,
            fontWeight: typography.fontWeight.medium,
            px: `${spacing.spacing_4}px`,
            py: `${spacing.spacing_3}px`
          }
        },
        contentProps: {
          sx: {
            px: `${spacing.spacing_4}px`,
            py: 0
          }
        },
        dialogActionsProps: {
          sx: {
            px: `${spacing.spacing_4}px`,
            pt: `${spacing.spacing_3}px`,
            pb: `${spacing.spacing_4}px`,
            [`& .${buttonClasses.root}`]: {
              ml: 0
            }
          }
        },
        cancellationButtonProps: {
          variant: 'text',
          sx: {
            height: spacing.spacing_4,
            px: `${spacing.spacing_2}px`,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            lineHeight: 1.5,
            borderRadius: borderRadius.button,
            textTransform: 'none',
            color: semanticColors.button.tertiary.fg,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              color: semanticColors.button.tertiary.fgHover
            },
            '&:active': {
              color: semanticColors.button.tertiary.fgPressed
            }
          }
        },
        confirmationButtonProps: {
          variant: 'contained',
          disabled: options?.confirmationButtonProps?.disabled,
          sx: {
            height: spacing.spacing_5,
            px: `${spacing.spacing_2}px`,
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            lineHeight: 1.5,
            borderRadius: borderRadius.button,
            textTransform: 'none',
            backgroundColor: options?.destructive
              ? semanticColors.button.destructive.bg
              : semanticColors.button.primary.bg,
            color: 'white',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: options?.destructive
                ? semanticColors.color.status.error.fg
                : semanticColors.button.primary.bgHover,
              boxShadow: 'none'
            },
            '&:active': {
              backgroundColor: options?.destructive
                ? semanticColors.color.status.error.fg
                : semanticColors.button.primary.bgPressed
            }
          }
        }
      };

      return confirm(modOptions);
    },
    [confirm]
  );
};
